import { useRef, type CSSProperties, type ReactElement } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { clsx } from "clsx";
import { color, radius, space, typography } from "@tokens/index";
import { SlidingPill } from "@primitives/index";
import { selectionBar } from "@compositions/queue-surfaces/listSurface";
import { SETTINGS_NAV_ENTRIES, SETTINGS_NAV_WIDTH, isSettingsEntryActive } from "./settingsNav";
import styles from "./SettingsSideNav.module.css";

/**
 * SettingsSideNav: the vertical category list on `expanded`+.
 *
 * Sticky beside the content column. The selected item uses the list
 * selection language: a `bg.selected` fill that slides between items and a
 * 3px accent bar on the leading edge.
 */

const stickyStyle: CSSProperties = {
    position: "sticky",
    top: 0,
    alignSelf: "start",
    width: SETTINGS_NAV_WIDTH,
    flexShrink: 0,
};

const trackStyle: CSSProperties = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: space[1],
};

const itemStyle = (isActive: boolean): CSSProperties => ({
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    gap: space[3],
    minHeight: 40,
    paddingInline: space[3],
    borderRadius: radius.md,
    textDecoration: "none",
    color: isActive ? color.fg.default : color.fg.muted,
    fontFamily: typography.family.system,
    fontSize: typography.scale.sm,
    fontWeight: isActive ? typography.weights.semibold : typography.weights.medium,
    lineHeight: 1,
    whiteSpace: "nowrap",
});

export function SettingsSideNav(): ReactElement {
    const { pathname } = useLocation();
    const trackRef = useRef<HTMLDivElement>(null);
    const activeIndex = SETTINGS_NAV_ENTRIES.findIndex((entry) =>
        isSettingsEntryActive(pathname, entry),
    );

    return (
        <nav aria-label="Settings sections" style={stickyStyle}>
            <div ref={trackRef} style={trackStyle}>
                <SlidingPill
                    containerRef={trackRef}
                    activeIndex={activeIndex}
                    itemSelector="[data-settings-nav-item]"
                    style={{ borderRadius: radius.md, background: color.bg.selected }}
                />
                {SETTINGS_NAV_ENTRIES.map((entry) => {
                    const Icon = entry.icon;
                    return (
                        <NavLink
                            key={entry.id}
                            to={entry.to}
                            data-settings-nav-item=""
                            className={clsx(styles.item)}
                            style={({ isActive }) => itemStyle(isActive)}
                        >
                            {({ isActive }) => (
                                <>
                                    {isActive && <span aria-hidden="true" style={selectionBar} />}
                                    <Icon size={16} strokeWidth={2} aria-hidden="true" />
                                    {entry.label}
                                </>
                            )}
                        </NavLink>
                    );
                })}
            </div>
        </nav>
    );
}
